import React, { useState, useEffect } from "react";
import cn from "classnames";
import styles from "./AccountPartner.module.sass";
import { use100vh } from "react-div-100vh";
import { useNavigate } from "react-router-dom";
import TextInput from "../../components/TextInput";
import PhoneInput from "../../components/PhoneInput";
import Image from "../../components/Image";
import { useAuth } from "../../contexts/AuthContext";
import { showSuccess, showError } from "../../services/toast.service";
import { updatePartnerForCurrentUser, getPartnerForCurrentUser } from "../../services/partner.service";
import { pb } from "../../services/pocketbase";
import Switch from "../../components/Switch";
import ModalCondition from "../../components/ModalConditionGeneral";
import ModalUseCondition from "../../components/ModalPolitiqueConfidentialite";

const settings = [
  {
    id: 0,
    title: "Conditions générales",
  },
  {
    id: 1,
    title: "Conditions d'utilisation",
  },
];

const CompleteAccountPartner = () => {
  const heightWindow = use100vh();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // États pour gérer les données du partenaire
  const [partner, setPartner] = useState({
    name: '',
    logo: '',
    address: '',
    complement_address: '',
    city: '',
    zip_code: '',
    website: '',
    email: '',
    phone: '',
    subscription_plan: 'basic',
    demo_account: false,
    is_active: true,
    admin_contact_name: '',
    admin_contact_email: '',
    admin_contact_phone: ''
  });
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [acceptConditions, setAcceptConditions] = useState(false);
  const [acceptUseConditions, setAcceptUseConditions] = useState(false);

  // États pour gérer l'affichage des modales
  const [visibleCondition, setVisibleCondition] = useState(false);
  const [visibleUseCondition, setVisibleUseCondition] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Détecter si l'affichage est en mode mobile
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Charger les données du partenaire depuis PocketBase
  useEffect(() => {
    const fetchPartnerData = async () => {
      try {
        setLoading(true);
        if (!pb.authStore.isValid) {
          console.error('Utilisateur non connecté');
          navigate('/signin');
          return;
        }

        const partnerData = await getPartnerForCurrentUser();
        setPartner({
          name: partnerData.name || '',
          logo: partnerData.logo || '',
          address: partnerData.address || '',
          complement_address: partnerData.complement_address || '',
          city: partnerData.city || '',
          zip_code: partnerData.zip_code || '',
          website: partnerData.website || '',
          email: partnerData.email || pb.authStore.model.email || '',
          phone: partnerData.phone || '',
          subscription_plan: partnerData.subscription_plan || 'basic',
          demo_account: partnerData.demo_account || false,
          is_active: partnerData.is_active !== undefined ? partnerData.is_active : true,
          admin_contact_name: partnerData.admin_contact_name || '',
          admin_contact_email: partnerData.admin_contact_email || '',
          admin_contact_phone: partnerData.admin_contact_phone || ''
        });
        
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors de la récupération des données:', error);
        setLoading(false);
        showError('Erreur lors de la récupération des données');
      }
    };

    fetchPartnerData();
  }, [navigate]);

  const handleChange = (id) => {
    if (id === 0) {
      setAcceptConditions(!acceptConditions);
    } else if (id === 1) {
      setAcceptUseConditions(!acceptUseConditions);
    }
  };

  // Gestionnaire de changement pour les champs de texte
  const handlePartnerChange = (e) => {
    const { name, value } = e.target;
    setPartner(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Fonction pour valider le formulaire
  const validateForm = () => {
    const missingFields = [];
    
    const requiredFields = [
      { field: 'name', label: 'Nom de l\'entreprise' },
      { field: 'admin_contact_name', label: 'Nom du contact administrateur' },
      { field: 'admin_contact_email', label: 'Email du contact administrateur' },
      { field: 'admin_contact_phone', label: 'Téléphone du contact administrateur' },
      { field: 'address', label: 'Adresse' },
      { field: 'zip_code', label: 'Code postal' },
      { field: 'city', label: 'Ville' },
      { field: 'email', label: 'Email de l\'entreprise' },
      { field: 'phone', label: 'Téléphone de l\'entreprise' }
    ];
    
    requiredFields.forEach(({ field, label }) => {
      if (!partner[field] || partner[field] === '') {
        missingFields.push(label);
      }
    });
    
    if (!acceptConditions || !acceptUseConditions) {
      missingFields.push('Acceptation des conditions générales et conditions d\'utilisation');
    }
    
    return missingFields;
  };

  // Sauvegarder les modifications
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const missingFields = validateForm();
    if (missingFields.length > 0) {
      showError(`Veuillez remplir tous les champs obligatoires: ${missingFields.join(', ')}`);
      return;
    }
    
    setIsSaving(true);

    try {
      // Mettre à jour les informations du partenaire
      await updatePartnerForCurrentUser(partner);
      showSuccess('Informations mises à jour avec succès');

      // Mettre à jour l'état first_connexion de l'utilisateur
      try {
        await pb.collection('users').update(pb.authStore.model.id, {
          first_connexion: false,
          conditionGeneral: true,
          conditionUtilisation: true
        });
      } catch (userUpdateError) {
        console.error('Erreur lors de la mise à jour des informations utilisateur:', userUpdateError);
      }

      // Rediriger vers l'étape suivante (création du centre)
      navigate('/partner/add-center');

    } catch (error) {
      console.error('Erreur lors de la sauvegarde des informations du partenaire:', error);
      showError('Erreur lors de la sauvegarde: ' + (error.message || 'Veuillez vérifier vos informations'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.row}>
      <div
        className={styles.col}
        style={{
          backgroundImage: `url('/images/content/loginbg.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          minHeight: heightWindow,
          flex: 1
        }}
      />
      <div className={styles.col} style={{ minHeight: heightWindow, flex: 1.5, overflow: 'auto', maxHeight: '100vh' }}>
        <div className={styles.head}>
          <div className={styles.logo}>
            <Image
              className={styles.pic}
              src="/images/logo.png"
              srcDark="/images/logo.png"
              alt="Core"
            />
          </div>
          <div className={cn("h2", styles.title)}>Compléter votre profil partenaire</div>
        </div>

        {loading ? (
          <div className={styles.wrapper}>
            <div>Chargement des informations...</div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className={styles.body}>
              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  Informations de l'entreprise
                </h3>
                <div className={styles.formGrid}>
                  <TextInput
                    className={styles.field}
                    label="Nom de l'entreprise"
                    name="name"
                    type="text"
                    placeholder="Nom de votre entreprise"
                    value={partner.name}
                    onChange={handlePartnerChange}
                    required
                  />
                  <TextInput
                    className={styles.field}
                    label="Email de l'entreprise"
                    name="email"
                    type="email"
                    placeholder="contact@entreprise.com"
                    value={partner.email}
                    onChange={handlePartnerChange}
                    required
                  />
                  <PhoneInput
                    className={styles.field}
                    label="Téléphone de l'entreprise"
                    name="phone"
                    placeholder="Numéro de téléphone de l'entreprise"
                    value={partner.phone}
                    onChange={(phone) => {
                      setPartner(prev => ({
                        ...prev,
                        phone: phone.target.value
                      }));
                    }}
                    required
                  />
                  <TextInput
                    className={styles.field}
                    label="Site web"
                    name="website"
                    type="url"
                    placeholder="https://www.entreprise.com"
                    value={partner.website}
                    onChange={handlePartnerChange}
                  />
                </div>
              </div>

              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  Contact administrateur
                </h3>
                <div className={styles.formGrid}>
                  <TextInput
                    className={styles.field}
                    label="Nom du contact administrateur"
                    name="admin_contact_name"
                    type="text"
                    placeholder="Nom et prénom du contact administrateur"
                    value={partner.admin_contact_name}
                    onChange={handlePartnerChange}
                    required
                  />
                  <TextInput
                    className={styles.field}
                    label="Email du contact administrateur"
                    name="admin_contact_email"
                    type="email"
                    placeholder="admin@entreprise.com"
                    value={partner.admin_contact_email}
                    onChange={handlePartnerChange}
                    required
                  />
                  <PhoneInput
                    className={styles.field}
                    label="Téléphone du contact administrateur"
                    name="admin_contact_phone"
                    placeholder="Numéro de téléphone de l'administrateur"
                    value={partner.admin_contact_phone}
                    onChange={(phone) => {
                      setPartner(prev => ({
                        ...prev,
                        admin_contact_phone: phone.target.value
                      }));
                    }}
                    required
                  />
                </div>
              </div>

              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  Adresse de l'entreprise
                </h3>
                <div className={styles.formGrid}>
                  <TextInput
                    className={styles.field}
                    label="Adresse"
                    name="address"
                    type="text"
                    placeholder="Adresse de l'entreprise"
                    value={partner.address}
                    onChange={handlePartnerChange}
                    required
                  />
                  <TextInput
                    className={styles.field}
                    label="Complément d'adresse"
                    name="complement_address"
                    type="text"
                    placeholder="Bâtiment, étage, appartement..."
                    value={partner.complement_address}
                    onChange={handlePartnerChange}
                  />
                  <TextInput
                    className={styles.field}
                    label="Code postal"
                    name="zip_code"
                    type="text"
                    placeholder="Code postal"
                    value={partner.zip_code}
                    onChange={handlePartnerChange}
                    required
                  />
                  <TextInput
                    className={styles.field}
                    label="Ville"
                    name="city"
                    type="text"
                    placeholder="Ville"
                    value={partner.city}
                    onChange={handlePartnerChange}
                    required
                  />
                </div>
              </div>

              <div className={styles.section} style={{ marginTop: '20px' }}>
                <h3 className={styles.sectionTitle}>
                  Règlement et conditions générales
                </h3>

                {settings.map((x, index) => (
                  <div className={styles.line} key={index}>
                    <div
                      className={styles.title}
                      onClick={() => {
                        if (x.id === 0) {
                          setVisibleCondition(true);
                        } else if (x.id === 1) {
                          setVisibleUseCondition(true);
                        }
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {x.title}
                    </div>
                    <Switch
                      className={styles.switch}
                      value={x.id === 0 ? acceptConditions : acceptUseConditions}
                      onChange={() => handleChange(x.id)}
                    />
                  </div>
                ))}

                {/* Modales pour les conditions et le règlement */}
                <ModalCondition
                  visible={visibleCondition}
                  onClose={() => setVisibleCondition(false)}
                  title="Conditions générales"
                  figcaption="Veuillez lire attentivement les conditions générales avant d'accepter."
                />

                <ModalUseCondition
                  visible={visibleUseCondition}
                  onClose={() => setVisibleUseCondition(false)}
                  title="Conditions d'utilisation"
                  figcaption="Veuillez lire attentivement les conditions d'utilisation avant d'accepter."
                />
              </div>

              {acceptConditions && acceptUseConditions ? (
                <div className={styles.buttonContainer}>
                  <button
                    className={cn("button", styles.button)}
                    type="submit"
                    disabled={isSaving}
                  >
                    {isSaving ? "Sauvegarde en cours..." : "Continuer"}
                  </button>
                </div>
              ) : (
                <div className={styles.buttonContainer} style={{ opacity: 0.7, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexDirection: isMobile ? 'column' : 'row' }}>
                  <div className={styles.infoText}>
                    Veuillez accepter les conditions générales et les conditions d'utilisation pour continuer
                  </div>
                  <button
                    className={cn("button", styles.button)}
                    type="button"
                    disabled={true}
                    style={{ cursor: 'not-allowed', marginLeft: '15px' }}
                  >
                    Continuer
                  </button>
                </div>
              )}
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CompleteAccountPartner;
